'use client';

import { useState, useRef, useEffect } from 'react';

import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuthStore } from '../../stores/authStore';
import { useLanguageStore, SUPPORTED_LANGUAGES, type LanguageCode } from '../../stores/languageStore';
import { subscriptionApi } from '../../services/api';
import { useToastActions } from '../ui/Toast';
import { getAvatarUrl } from '../../utils/avatar';
import { isLocalDeployment } from '../../config';
import { 
  FlowArrow, 
  SignOut, 
  User,
  Users,
  House,
  CaretLeft,
  CaretRight,
  Gear,
  Crown,
  ChatCircleText,
  Translate,
  Check,
} from '@phosphor-icons/react';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string; weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone' }>;
}

const navItems: NavItem[] = [
  { path: '/home', label: '首页', icon: House },
  { path: '/workflows', label: '工作流', icon: FlowArrow },
  // { path: '/data', label: '数据管理', icon: Database },
];

const SIDEBAR_EXPANDED = 288; // w-72
const SIDEBAR_COLLAPSED = 64;  // w-16

// Animated Nav Link with spring physics
function AnimatedNavLink({ item, isActive, collapsed }: { item: NavItem; isActive: boolean; collapsed: boolean }) {
  const Icon = item.icon;
  
  return (
    <NavLink
      to={item.path}
      title={collapsed ? item.label : undefined}
      className={`relative flex items-center py-3 rounded-xl text-sm font-medium transition-colors duration-200 ${collapsed ? 'justify-center px-0' : 'gap-3 px-4'}`}
    >
      {isActive && (
        <motion.div
          layoutId="activeNav"
          className="absolute inset-0 bg-zinc-900 rounded-xl"
          initial={false}
          transition={{
            type: 'spring',
            stiffness: 500,
            damping: 35,
          }}
        />
      )}
      <span className={`relative z-10 flex items-center gap-3 ${isActive ? 'text-white' : 'text-zinc-600 hover:text-zinc-900'}`}>
        <Icon className="w-5 h-5 flex-shrink-0" weight={isActive ? 'fill' : 'regular'} />
        <AnimatePresence initial={false}>
          {!collapsed && (
            <motion.span
              key="label"
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden whitespace-nowrap"
            >
              {item.label}
            </motion.span>
          )}
        </AnimatePresence>
      </span>
    </NavLink>
  );
}

// 计划名称映射
const planNames: Record<string, string> = {
  free: '免费版',
  pro: '专业版',
  enterprise: '企业版',
};

export function MainLayout() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [, setUserMenuPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { success: toastSuccess } = useToastActions();
  
  // 订阅计划状态
  const [currentPlan, setCurrentPlan] = useState<string>('free');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSettings = () => {
    navigate('/settings');
    setShowUserMenu(false);
  };

  // 语言设置 - 使用全局状态
  const { language, setLanguage } = useLanguageStore();
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  const handleUpgrade = () => {
    navigate('/upgrade');
    setShowUserMenu(false);
  };

  const handleFeedback = () => {
    navigate('/feedback');
    setShowUserMenu(false);
  };

  const handleLanguage = () => {
    setShowLanguageMenu(true);
  };

  const openUserMenu = () => {
    const el = userMenuRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      const menuHeight = 280;
      setUserMenuPosition({
        top: rect.top - menuHeight - 8,
        left: rect.left,
        width: collapsed ? 192 : rect.width,
      });
      setShowUserMenu(true);
    }
  };

  const toggleUserMenu = () => {
    if (showUserMenu) {
      setShowUserMenu(false);
      setShowLanguageMenu(false);
      setUserMenuPosition(null);
    } else {
      openUserMenu();
    }
  };

  const handleLanguageSelect = (langCode: string) => {
    setLanguage(langCode as LanguageCode);
    setShowLanguageMenu(false);
    toastSuccess('语言切换成功');
  };

  // 加载订阅计划
  useEffect(() => {
    const loadSubscription = async () => {
      try {
        const res = await subscriptionApi.getCurrentSubscription();
        if (res.code === 0 && res.data) {
          setCurrentPlan(res.data.plan || 'free');
        }
      } catch (error) {
        console.error('Failed to load subscription:', error);
      }
    };
    loadSubscription();
  }, []);

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
        setShowLanguageMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;

  return (
    <div className="h-full bg-zinc-50 flex overflow-hidden">
      {/* Sidebar - Bento Style */}
      <motion.aside 
        className="fixed left-0 top-0 h-full bg-white border-r border-zinc-200/50 z-50 flex flex-col overflow-hidden"
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1, width: sidebarWidth }}
        transition={{ type: 'spring', stiffness: 100, damping: 25 }}
      >
        {/* Logo Area */}
        <div className={`p-4 flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
          <motion.div 
            className={`flex items-center gap-3 ${collapsed ? '' : 'px-2'}`}
            whileHover={{ scale: 1.02 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <div className="w-10 h-10 rounded-xl bg-zinc-900 flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img src="/logo.svg" alt="Logo" className="w-6 h-6" />
            </div>
            <AnimatePresence initial={false}>
              {!collapsed && (
                <motion.div
                  key="logo-text"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <span className="text-lg font-bold text-zinc-900 tracking-tight whitespace-nowrap">DataFlow</span>
                  <span className="text-xs text-zinc-400 block -mt-0.5 whitespace-nowrap">Engine</span>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Collapse Toggle Button */}
          {!collapsed && (
            <motion.button
              onClick={() => setCollapsed(true)}
              className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors flex-shrink-0"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="折叠侧栏"
            >
              <CaretLeft className="w-4 h-4" />
            </motion.button>
          )}
        </div>

        {/* Expand button when collapsed */}
        {collapsed && (
          <div className="flex justify-center pb-2">
            <motion.button
              onClick={() => setCollapsed(false)}
              className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title="展开侧栏"
            >
              <CaretRight className="w-4 h-4" />
            </motion.button>
          </div>
        )}

        {/* Navigation */}
        <nav className={`flex-1 py-4 space-y-1 ${collapsed ? 'px-2' : 'px-4'}`}>
          <div className="mb-6">
            <AnimatePresence initial={false}>
              {!collapsed && (
                <motion.p
                  key="nav-label"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="px-4 text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3"
                >
                  主菜单
                </motion.p>
              )}
            </AnimatePresence>
            <div className="space-y-1">
              {navItems.map((item) => (
                <AnimatedNavLink
                  key={item.path}
                  item={item}
                  isActive={location.pathname.startsWith(item.path)}
                  collapsed={collapsed}
                />
              ))}
            </div>
          </div>

          {/* Quick Stats - Bento Card */}
          {/* <AnimatePresence initial={false}>
            {!collapsed && (
              <motion.div
                key="stats-card"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="px-4"
              >
                <motion.div 
                  className="p-4 rounded-2xl bg-zinc-50 border border-zinc-100"
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <p className="text-xs font-medium text-zinc-500 mb-3">今日处理</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-zinc-900">2,847</span>
                    <span className="text-xs text-emerald-600 font-medium">+12%</span>
                  </div>
                  <div className="mt-3 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                    <motion.div 
                      className="h-full bg-emerald-500 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: '68%' }}
                      transition={{ delay: 0.5, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    />
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence> */}
        </nav>

        {/* User Profile - Bottom */}
        <div className={`p-3 border-t border-zinc-100 ${collapsed ? 'flex justify-center' : ''}`} ref={userMenuRef}>
          {collapsed ? (
            <div className="relative">
              <motion.button
                onClick={toggleUserMenu}
                className="relative group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title={user?.username || '用户'}
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-50 flex items-center justify-center border border-emerald-100 cursor-pointer overflow-hidden">
                  {user?.avatar ? (
                    <img src={getAvatarUrl(user.avatar)} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-emerald-600" weight="fill" />
                  )}
                </div>
              </motion.button>
              
              {/* Collapsed User Menu Popup */}
              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 10 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    className="absolute bottom-full left-0 mb-2 w-48 bg-white rounded-xl border border-zinc-200 shadow-xl shadow-zinc-900/10 py-1 z-50"
                  >
                    <div className="px-3 py-2 border-b border-zinc-100">
                      <p className="text-sm font-medium text-zinc-900">{user?.username || '用户'}</p>
                      <p className="text-xs text-zinc-400">{planNames[currentPlan] || '免费版'}</p>
                    </div>
                    {!isLocalDeployment && (
                      <>
                        <button onClick={handleUpgrade} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                          <Crown className="w-4 h-4 text-amber-500" />
                          订阅升级
                        </button>
                        <button onClick={handleFeedback} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                          <ChatCircleText className="w-4 h-4 text-blue-500" />
                          用户反馈
                        </button>
                      </>
                    )}
                    <button onClick={handleLanguage} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                      <Translate className="w-4 h-4 text-purple-500" />
                      语言切换
                    </button>
                    {isLocalDeployment && user?.role === 'admin' && (
                      <button onClick={() => { navigate('/users'); setShowUserMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                        <Users className="w-4 h-4 text-emerald-500" />
                        用户管理
                      </button>
                    )}
                    <button onClick={handleSettings} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                      <Gear className="w-4 h-4 text-zinc-500" />
                      设置
                    </button>
                    <div className="border-t border-zinc-100 mt-1 pt-1">
                      <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
                        <SignOut className="w-4 h-4" />
                        退出
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Language Submenu (Collapsed) - 替换主菜单内容 */}
              <AnimatePresence>
                {showUserMenu && showLanguageMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    className="absolute bottom-full left-0 mb-2 w-48 bg-white rounded-xl border border-zinc-200 shadow-xl shadow-zinc-900/10 py-1 z-50"
                  >
                    <div className="px-3 py-2 border-b border-zinc-100 flex items-center justify-between">
                      <p className="text-sm font-medium text-zinc-900">选择语言</p>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setShowLanguageMenu(false); }}
                        className="text-xs text-zinc-400 hover:text-zinc-600"
                      >
                        返回
                      </button>
                    </div>
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageSelect(lang.code)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                          language === lang.code ? 'bg-emerald-50 text-emerald-700' : 'text-zinc-700 hover:bg-zinc-50'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span>{lang.name}</span>
                        {language === lang.code && (
                          <Check className="w-4 h-4 ml-auto" weight="bold" />
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <div className="relative">
              <motion.button 
                onClick={toggleUserMenu}
                className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-zinc-50 transition-colors cursor-pointer group"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-50 flex items-center justify-center border border-emerald-100 overflow-hidden">
                    {user?.avatar ? (
                      <img src={getAvatarUrl(user.avatar)} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-5 h-5 text-emerald-600" weight="fill" />
                    )}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-zinc-900">
                      {user?.username || '用户'}
                    </p>
                    <p className="text-xs text-zinc-400">{planNames[currentPlan] || '免费版'}</p>
                  </div>
                </div>
                <Gear className="w-4 h-4 text-zinc-400 group-hover:text-zinc-600 transition-colors" />
              </motion.button>
              
              {/* Expanded User Menu Popup */}
              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 10 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-xl border border-zinc-200 shadow-xl shadow-zinc-900/10 py-1 z-50"
                  >
                    {!isLocalDeployment && (
                      <>
                        <button onClick={handleUpgrade} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                          <Crown className="w-4 h-4 text-amber-500" />
                          订阅升级
                        </button>
                        <button onClick={handleFeedback} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                          <ChatCircleText className="w-4 h-4 text-blue-500" />
                          用户反馈
                        </button>
                      </>
                    )}
                    <button onClick={handleLanguage} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                      <Translate className="w-4 h-4 text-purple-500" />
                      语言切换
                    </button>
                    {isLocalDeployment && user?.role === 'admin' && (
                      <button onClick={() => { navigate('/users'); setShowUserMenu(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                        <Users className="w-4 h-4 text-emerald-500" />
                        用户管理
                      </button>
                    )}
                    <button onClick={handleSettings} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors">
                      <Gear className="w-4 h-4 text-zinc-500" />
                      设置
                    </button>
                    <div className="border-t border-zinc-100 mt-1 pt-1">
                      <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
                        <SignOut className="w-4 h-4" />
                        退出登录
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Language Submenu (Expanded) - 替换主菜单内容 */}
              <AnimatePresence>
                {showUserMenu && showLanguageMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                    className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-xl border border-zinc-200 shadow-xl shadow-zinc-900/10 py-1 z-50"
                  >
                    <div className="px-3 py-2 border-b border-zinc-100 flex items-center justify-between">
                      <p className="text-sm font-medium text-zinc-900">选择语言</p>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setShowLanguageMenu(false); }}
                        className="text-xs text-zinc-400 hover:text-zinc-600"
                      >
                        返回
                      </button>
                    </div>
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageSelect(lang.code)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                          language === lang.code ? 'bg-emerald-50 text-emerald-700' : 'text-zinc-700 hover:bg-zinc-50'
                        }`}
                      >
                        <span className="text-lg">{lang.flag}</span>
                        <span>{lang.name}</span>
                        {language === lang.code && (
                          <Check className="w-4 h-4 ml-auto" weight="bold" />
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </motion.aside>

      {/* Main Content Area */}
      <motion.main
        className="flex-1 h-full overflow-hidden bg-zinc-50"
        animate={{ marginLeft: sidebarWidth }}
        transition={{ type: 'spring', stiffness: 100, damping: 25 }}
      >
        <Outlet />
      </motion.main>
    </div>
  );
}
